import changelogData from "../app/landing/changelogEntries.json";
import { applyChangelogGeoSeo, formatChangelogWeekLabel } from "./geoSeo";

type ChangelogEntry = (typeof changelogData.entries)[number];

type ChangelogWeek = {
  weekKey: string;
  label: string;
  entries: ChangelogEntry[];
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function changelogWeekId(weekKey: string): string {
  return `changelog-week-${weekKey}`;
}

function formatEntryDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const date = new Date(y, m - 1, d, 12, 0, 0, 0);
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function renderTags(tags: string[]): string {
  if (!tags.length) return "";
  const pills = tags
    .map((tag) => `<span class="changelog-tag">${escapeHtml(tag)}</span>`)
    .join("");
  return `<div class="changelog-tags" aria-label="Categories">${pills}</div>`;
}

function renderEntryBody(entry: ChangelogEntry): string {
  return `${renderTags(entry.tags)}
    <h3 class="changelog-entry-title">${escapeHtml(entry.title)}</h3>
    <p class="changelog-entry-summary">${escapeHtml(entry.summary)}</p>`;
}

function renderEntry(entry: ChangelogEntry): string {
  return `<article class="changelog-entry" id="${escapeHtml(entry.id)}">
    ${renderEntryBody(entry)}
  </article>`;
}

function groupEntriesByDate(entries: ChangelogEntry[]): Array<{ date: string; label: string; entries: ChangelogEntry[] }> {
  const map = new Map<string, ChangelogEntry[]>();
  for (const entry of entries) {
    const list = map.get(entry.date) ?? [];
    list.push(entry);
    map.set(entry.date, list);
  }

  return [...map.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, dayEntries]) => ({
      date,
      label: formatEntryDate(date),
      entries: dayEntries.sort((a, b) => a.title.localeCompare(b.title)),
    }));
}

function renderDayGroup(day: { date: string; label: string; entries: ChangelogEntry[] }): string {
  const entryHtml = day.entries.map(renderEntry).join("");
  return `<div class="changelog-day">
    <p class="changelog-day-date">${escapeHtml(day.label)}</p>
    <div class="changelog-day-entries">${entryHtml}</div>
  </div>`;
}

function groupEntriesByWeek(entries: ChangelogEntry[]): ChangelogWeek[] {
  const map = new Map<string, ChangelogEntry[]>();
  for (const entry of entries) {
    const list = map.get(entry.weekKey) ?? [];
    list.push(entry);
    map.set(entry.weekKey, list);
  }

  return [...map.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([weekKey, weekEntries]) => ({
      weekKey,
      label: formatChangelogWeekLabel(weekKey),
      entries: weekEntries.sort((a, b) => b.date.localeCompare(a.date) || a.title.localeCompare(b.title)),
    }));
}

function renderSidebarNav(weeks: ChangelogWeek[]): string {
  return weeks
    .map(
      (week) =>
        `<a href="#${escapeHtml(changelogWeekId(week.weekKey))}" class="toolkit-sidebar-link" data-changelog-nav="${escapeHtml(week.weekKey)}">${escapeHtml(week.label)}</a>`,
    )
    .join("");
}

function renderChangelogFeed(weeks: ChangelogWeek[]): string {
  return weeks
    .map(
      (week) =>
        `<section class="changelog-week" id="${escapeHtml(changelogWeekId(week.weekKey))}" data-changelog-week="${escapeHtml(week.weekKey)}" aria-labelledby="${escapeHtml(changelogWeekId(week.weekKey))}-heading">
          <h2 class="changelog-week-heading" id="${escapeHtml(changelogWeekId(week.weekKey))}-heading">${escapeHtml(week.label)}</h2>
          <div class="changelog-week-entries">
            ${groupEntriesByDate(week.entries).map(renderDayGroup).join("")}
          </div>
        </section>`,
    )
    .join("");
}

function getHeaderOffset(): number {
  const header = document.getElementById("marketing-header");
  return header ? header.getBoundingClientRect().height : 72;
}

function scrollToWeek(weekKey: string, smooth: boolean): void {
  const target = document.getElementById(changelogWeekId(weekKey));
  if (!target) return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const top = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset() - 16;
  window.scrollTo({ top: Math.max(0, top), behavior: smooth && !reduceMotion ? "smooth" : "auto" });
}

function setActiveNav(weekKey: string): void {
  document.querySelectorAll<HTMLElement>(".toolkit-sidebar-link[data-changelog-nav]").forEach((link) => {
    const isActive = link.dataset.changelogNav === weekKey;
    link.classList.toggle("is-active", isActive);
    if (isActive) {
      link.setAttribute("aria-current", "location");
    } else {
      link.removeAttribute("aria-current");
    }
  });
}

function getWeekKeyFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash.startsWith("changelog-week-")) return null;
  return hash.slice("changelog-week-".length) || null;
}

function navigateToWeek(weekKey: string, push = true): void {
  setActiveNav(weekKey);
  scrollToWeek(weekKey, true);
  const hash = `#${changelogWeekId(weekKey)}`;
  if (push) {
    window.history.pushState({ changelogWeek: weekKey }, "", hash);
  }
  closeMobileNav();
}

function closeMobileNav(): void {
  document.getElementById("changelog-sidebar")?.classList.remove("is-open");
  document.getElementById("changelog-sidebar-backdrop")?.setAttribute("hidden", "");
  document.getElementById("changelog-sidebar-backdrop")?.setAttribute("aria-hidden", "true");
  document.getElementById("changelog-mobile-nav-toggle")?.setAttribute("aria-expanded", "false");
}

function openMobileNav(): void {
  document.getElementById("changelog-sidebar")?.classList.add("is-open");
  const backdrop = document.getElementById("changelog-sidebar-backdrop");
  backdrop?.removeAttribute("hidden");
  backdrop?.setAttribute("aria-hidden", "false");
  document.getElementById("changelog-mobile-nav-toggle")?.setAttribute("aria-expanded", "true");
}

function setupChangelogNavigation(weeks: ChangelogWeek[]): void {
  document.querySelectorAll<HTMLAnchorElement>("[data-changelog-nav]").forEach((link) => {
    link.addEventListener("click", (event) => {
      const weekKey = link.dataset.changelogNav;
      if (!weekKey) return;
      event.preventDefault();
      navigateToWeek(weekKey);
    });
  });

  window.addEventListener("popstate", () => {
    const weekKey = getWeekKeyFromHash() ?? weeks[0]?.weekKey;
    if (!weekKey) return;
    setActiveNav(weekKey);
    scrollToWeek(weekKey, false);
  });
}

function setupChangelogScrollSpy(weeks: ChangelogWeek[]): void {
  const sections = [...document.querySelectorAll<HTMLElement>("[data-changelog-week]")];
  if (!sections.length) return;

  let ticking = false;

  const updateActiveSection = (): void => {
    const offset = getHeaderOffset() + 20;
    let activeWeekKey = sections[0]?.dataset.changelogWeek ?? weeks[0]?.weekKey ?? "";

    for (const section of sections) {
      if (section.getBoundingClientRect().top <= offset) {
        activeWeekKey = section.dataset.changelogWeek ?? activeWeekKey;
      } else {
        break;
      }
    }

    const current = document.querySelector<HTMLElement>(".toolkit-sidebar-link.is-active[data-changelog-nav]")
      ?.dataset.changelogNav;
    if (current !== activeWeekKey) {
      setActiveNav(activeWeekKey);
    }
    ticking = false;
  };

  const scheduleUpdate = (): void => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateActiveSection);
  };

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", scheduleUpdate);
  updateActiveSection();
}

function setupMobileNav(): void {
  const toggle = document.getElementById("changelog-mobile-nav-toggle");
  const backdrop = document.getElementById("changelog-sidebar-backdrop");
  toggle?.addEventListener("click", () => {
    if (document.getElementById("changelog-sidebar")?.classList.contains("is-open")) {
      closeMobileNav();
    } else {
      openMobileNav();
    }
  });
  backdrop?.addEventListener("click", closeMobileNav);
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && document.getElementById("changelog-sidebar")?.classList.contains("is-open")) {
      closeMobileNav();
    }
  });
}

export function isChangelogPath(): boolean {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  return path === "/changelog";
}

export function showChangelogPage(): void {
  const marketingPage = document.getElementById("static-marketing-page");
  const pricingPage = document.getElementById("static-pricing-page");
  const toolkitPage = document.getElementById("static-toolkit-page");
  const securityPage = document.getElementById("static-security-page");
  const changelogPage = document.getElementById("static-changelog-page");
  if (marketingPage) marketingPage.hidden = true;
  if (pricingPage) pricingPage.hidden = true;
  if (toolkitPage) toolkitPage.hidden = true;
  if (securityPage) securityPage.hidden = true;
  if (changelogPage) changelogPage.hidden = false;
}

export function setupChangelogPage(): void {
  const nav = document.getElementById("changelog-sidebar-nav");
  const meta = document.getElementById("changelog-page-meta");
  const main = document.getElementById("changelog-main");
  if (!nav || !meta || !main) return;

  const entries = changelogData.entries as ChangelogEntry[];
  const weeks = groupEntriesByWeek(entries);

  meta.textContent = `${entries.length} updates documented · ${weeks.length} weeks`;
  nav.innerHTML = renderSidebarNav(weeks);
  main.innerHTML = `<div class="changelog-feed">${renderChangelogFeed(weeks)}</div>`;
  applyChangelogGeoSeo(entries);

  setupChangelogNavigation(weeks);
  setupChangelogScrollSpy(weeks);
  setupMobileNav();

  const initialWeekKey = getWeekKeyFromHash() ?? weeks[0]?.weekKey;
  if (!initialWeekKey) return;

  requestAnimationFrame(() => {
    setActiveNav(initialWeekKey);
    scrollToWeek(initialWeekKey, false);
    if (getWeekKeyFromHash()) {
      window.history.replaceState({ changelogWeek: initialWeekKey }, "", `#${changelogWeekId(initialWeekKey)}`);
    }
  });
}
